import { useState, useEffect, useRef } from 'react';

/**
 * Ensures a loading state is shown for a minimum duration to prevent flickering.
 * - When isLoading becomes true: immediately returns true
 * - When isLoading becomes false: waits until minimum duration has passed before returning false
 *
 * @param isLoading - The actual loading state to track
 * @param minimumDuration - Minimum time in ms to show loading (default: 1000)
 */
export const useMinimumLoading = (isLoading: boolean, minimumDuration: number = 1000): boolean => {
  const [showLoading, setShowLoading] = useState(false);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (isLoading) {
      startTimeRef.current = Date.now();
      setShowLoading(true);
      return;
    }

    if (!startTimeRef.current) {
      return;
    }

    const elapsed = Date.now() - startTimeRef.current;
    const remaining = minimumDuration - elapsed;

    if (remaining > 0) {
      const timeout = setTimeout(() => {
        setShowLoading(false);
        startTimeRef.current = null;
      }, remaining);
      return () => clearTimeout(timeout);
    }

    setShowLoading(false);
    startTimeRef.current = null;
  }, [isLoading, minimumDuration]);

  return showLoading;
};
